// THE ONLY PLACE IN apps/web/src THAT TOUCHES THE RAW `__BUILD_INFO__` GLOBAL.
//
// `buildInfo.test.ts` enforces that: it fails if any other file under `src/`
// reads the identifier. Read it through `buildInfo()` instead.
//
// ── WHY THE RULE EXISTS ────────────────────────────────────────────────────
//
// `__BUILD_INFO__` is a Vite `define` from `apps/web/vite.config.ts`, and that
// is not the only thing that compiles this directory. `vitest` runs these files
// under its own config, and `tools/admin` reaches into shared code through its
// own aliases. A module that reads the global unguarded compiles fine in the
// app that supplies it and dies in the one that does not — with a `TS2304` at
// compile time and a `ReferenceError` at module load, while typecheck, lint,
// the test suite and a production build all stay green, because none of them
// compile the consumer that breaks. The Puzzle Site lost an admin page to
// exactly this on 2026-08-08; this file is its fix, brought over on Ken's
// suggestion when he asked for the same build stamp here.
//
// ── THE TRAP INSIDE THE FIX ────────────────────────────────────────────────
//
// THE IDENTIFIER MUST STAY BARE. `define` is a TEXTUAL substitution of exactly
// `__BUILD_INFO__`. Writing `globalThis.__BUILD_INFO__` reads tidier and is
// wrong: it is not substituted, so it evaluates to `undefined` even in the app
// that DOES define it, silently disabling whatever depended on the value with
// nothing to notice. `typeof` on an undeclared identifier is legal and does not
// throw, so it is the one safe probe — and where the define IS present it folds
// to a compile-time constant and costs nothing.

/** Build identifiers, shaped exactly like the `__BUILD_INFO__` define. */
export interface BuildInfo {
  sha: string;
  branch: string;
  builtAt: string;
  committedAt: string;
  cfEnvironment: 'production' | 'preview' | 'local';
}

/**
 * What a consumer that never injected the define sees.
 *
 * `'unknown'` rather than an empty string, matching what `vite.config.ts`
 * already substitutes when git is unavailable at build time — so downstream
 * code has ONE absent-value case to handle rather than two. `'local'` is the
 * honest answer for a build that is not a Cloudflare one.
 */
const ABSENT: BuildInfo = {
  sha: 'unknown',
  branch: 'unknown',
  builtAt: 'dev',
  committedAt: 'unknown',
  cfEnvironment: 'local',
};

/** This build's identifiers, or `ABSENT` where the define was never injected. Never throws. */
export function buildInfo(): BuildInfo {
  if (typeof __BUILD_INFO__ === 'undefined') return ABSENT;
  return __BUILD_INFO__;
}

/** The short form, for a screen. `unknown` stays whole rather than becoming `unknow`. */
export function shortSha(sha: string): string {
  return sha === 'unknown' ? sha : sha.slice(0, 7);
}
